import React, { useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useDispatch } from "react-redux";
import { hideModal } from "../../data/modal";
import { updateTerminal as updateTerminalAction, createTerminal as createTerminalAction } from "../../data/terminals";
import { Terminal } from "../../types/Terminal";
import { Tenant } from "../../types/Tenant";

import {
    Box,
    Button,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import { Save as SaveIcon, Close as CloseIcon } from "@mui/icons-material";

interface GenesisTerminalFormProps {
    mode: 'create' | 'edit';
    type: string;
    item?: Terminal;
    tenants?: Tenant[];
    onSave?: (data: Partial<Terminal>) => void;
}

const getTenantId = (t: any): string => {
    if (!t) return '';
    if (typeof t === 'string') return t;
    return (t as any)["@id"] || (t as any).id || '';
};

const getInitialTenant = (tenant: any): string[] => {
    if (!tenant) return [];
    if (Array.isArray(tenant)) {
        return tenant.map(getTenantId);
    }
    return [getTenantId(tenant)];
};

const toNullableFloat = (value: any) => {
    if (value === null || value === undefined || value === "") {
        return null;
    }
}

const GenesisTerminalForm = ({ mode, item, tenants = [], onSave }: GenesisTerminalFormProps) => {
    const dispatch = useDispatch();
    const isCreate = mode === 'create';

    const {
        handleSubmit,
        control,
        formState: { isSubmitting },
        reset,
    } = useForm<Partial<Terminal>>({
        defaultValues: isCreate
            ? {
                name: '',
                abbreviation: '',
                city: '',
                latitude: null,
                longitude: null,
                tenant: [],
            }
            : {
                name: item?.name || '',
                abbreviation: item?.abbreviation || '',
                city: item?.city || '',
                latitude: item?.latitude || null,
                longitude: item?.longitude || null,
                tenant: getInitialTenant(item?.tenant),
            },
    });

    useEffect(() => {
        if (!isCreate && item) {
            reset({
                name: item.name || '',
                abbreviation: item.abbreviation || '',
                city: item.city || '',
                latitude: item.latitude || null,
                longitude: item.longitude || null,
                tenant: getInitialTenant(item.tenant),
            });
        }
    }, [item, isCreate, reset]);

    const handleClose = () => {
        dispatch(hideModal());
    };

    const onSubmit = (data: Partial<Terminal>) => {
        // Prepare tenant references
        const tenantData = data.tenant && data.tenant.length > 0
            ? data.tenant.map(t => ({ "@id": t }))
            : undefined;

        const normalizedContacts = Array.isArray(data.contacts)
            ? data.contacts
                .map((contact: any) => ({
                    ...(contact?.["@id"] ? { "@id": contact["@id"] } : {}),
                    ...(contact?.id ? { id: contact.id } : {}),
                    name: contact?.name?.trim() || null,
                    phoneNumber: contact?.phoneNumber?.trim() || null,
                    email: contact?.email?.trim() || null,
                }))
                .filter(
                    (contact: any) =>
                        contact["@id"] ||
                        contact.id ||
                        contact.name ||
                        contact.phoneNumber ||
                        contact.email
                )
            : [];

        const formData = {
            ...data,
            tenant: tenantData ? [0]["@id"] : '',
            latitude: toNullableFloat(data.latitude),
            longitude: toNullableFloat(data.longitude),
            contacts: normalizedContacts,
        };

        if (isCreate) {
            dispatch(createTerminalAction(formData, () => {
                dispatch(hideModal());
                if (onSave) onSave(formData);
            }) as any);
        } else {
            // For edit, we need to include the @id
            const updateData = {
                ...formData,
                "@id": item?.["@id"] || item?.id,
            };

            dispatch(updateTerminalAction(updateData) as any);
            if (onSave) onSave(updateData);
        }
    };

    return (
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
            <DialogTitle>
                {isCreate ? 'Create Terminal' : 'Edit Terminal'}
            </DialogTitle>

            <DialogContent>
                <Grid container spacing={2} sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="name"
                            control={control}
                            rules={{ required: 'Name is required' }}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    label="Name *"
                                    fullWidth
                                    error={!!error}
                                    helperText={error?.message}
                                    disabled={isSubmitting}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="abbreviation"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    label="Abbreviation"
                                    fullWidth
                                    error={!!error}
                                    helperText={error?.message}
                                    disabled={isSubmitting}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="city"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    label="City"
                                    fullWidth
                                    error={!!error}
                                    helperText={error?.message}
                                    disabled={isSubmitting}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="latitude"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    label="Latitude"
                                    fullWidth
                                    type="number"
                                    inputProps={{ step: 0.00001 }}
                                    error={!!error}
                                    helperText={error?.message}
                                    disabled={isSubmitting}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="longitude"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    {...field}
                                    label="Longitude"
                                    fullWidth
                                    type="number"
                                    inputProps={{ step: 0.00001 }}
                                    error={!!error}
                                    helperText={error?.message}
                                    disabled={isSubmitting}
                                />
                            )}
                        />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                        <Controller
                            name="tenant"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <FormControl fullWidth error={!!error}>
                                    <InputLabel>Tenant(s)</InputLabel>
                                    <Select
                                        {...field}
                                        label="Tenant(s)"
                                        multiple
                                        value={field.value || []}
                                        onChange={(e) => field.onChange(e.target.value)}
                                        disabled={isSubmitting}
                                        renderValue={(selected) => {
                                            const selectedTenants = tenants.filter(t =>
                                                (selected as string[]).includes(getTenantId(t))
                                            );
                                            return selectedTenants.map(t => t.name).join(', ');
                                        }}
                                    >
                                        {tenants.map((tenant) => (
                                            <MenuItem
                                                key={getTenantId(tenant)}
                                                value={getTenantId(tenant)}
                                            >
                                                {tenant.name}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            )}
                        />
                    </Grid>
                </Grid>
            </DialogContent>

            <DialogActions>
                <Button onClick={handleClose} disabled={isSubmitting} startIcon={<CloseIcon />}>
                    Cancel
                </Button>
                <Button
                    type="submit"
                    disabled={isSubmitting}
                    variant="contained"
                    color="primary"
                    startIcon={<SaveIcon />}
                >
                    {isSubmitting ? 'Saving...' : 'Save'}
                </Button>
            </DialogActions>
        </Box>
    );
};

export default GenesisTerminalForm;
